import React from 'react';
import { Button, Typography, Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import {
  BankOutlined,
  TeamOutlined,
  BarChartOutlined,
  LoginOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useSiteSettings } from '../../../context/SiteSettingsContext';
import SystemIcon from '../../../components/SystemIcon';
import './landing.css';

const { Title, Paragraph, Text } = Typography;

const FEATURES = [
  {
    icon: <BankOutlined />,
    title: 'จัดการข้อมูลวัด',
    desc: 'บันทึกและดูแลข้อมูลวัด ทั้งที่ตั้ง หมู่บ้าน ตำบล อำเภอ จังหวัด และประเภทของวัดอย่างเป็นระบบ',
  },
  {
    icon: <TeamOutlined />,
    title: 'ข้อมูลพระสงฆ์',
    desc: 'บริหารจัดการประวัติพระสงฆ์ สังกัดวัด การศึกษา และสมณศักดิ์ได้อย่างครบถ้วน',
  },
  {
    icon: <BarChartOutlined />,
    title: 'สถิติและรายงาน',
    desc: 'สรุปสถิติรายปี พร้อมกราฟภาพรวม และการจัดทำบัญชีกลางปีอย่างเป็นระเบียบ',
  },
];

const ABOUT_POINTS = [
  'จัดเก็บข้อมูลวัด พระสงฆ์ และสถิติรายปีไว้ในที่เดียว',
  'จัดลำดับวัดได้ตามขอบเขตสิทธิ์การใช้งาน',
  'นำเข้าและส่งออกข้อมูลสถิติด้วยไฟล์ Excel',
  'แสดงสรุปสถิติพร้อมกราฟภาพรวมในแดชบอร์ด',
];

const LandingPage = () => {
  const { settings } = useSiteSettings();

  return (
    <div className="landing-page">
      {/* ===== Hero ===== */}
      <section
        className="lp-hero"
        style={
          settings.hero_image
            ? {
                backgroundImage: `linear-gradient(rgba(58, 33, 12, 0.68), rgba(42, 24, 8, 0.60)), url(${settings.hero_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className="lp-hero-inner">
          <div className="lp-hero-badge">
            <SafetyCertificateOutlined />
            <span>สารสนเทศคณะสงฆ์ ภาค ๔-๕ (ธรรมยุต)</span>
          </div>
          <h1 className="lp-hero-title">
            ระบบสารสนเทศคณะสงฆ์
            <br />
            <span className="lp-hero-title-gold">ภาค ๔-๕ (ธรรมยุต)</span>
          </h1>
          <p className="lp-hero-subtitle">
            ระบบบริหารจัดการข้อมูลวัด พระสงฆ์ และสถิติรายปีของคณะสงฆ์
            พร้อมแสดงผลแนวโน้มและภาพรวมอย่างครบถ้วน เพื่อสนับสนุนการบริหารกิจการคณะสงฆ์
          </p>
          <div className="lp-hero-actions">
            <Link to="/login">
              <Button type="primary" size="large" className="lp-btn-gold" icon={<LoginOutlined />}>
                เริ่มต้นใช้งาน
              </Button>
            </Link>
            <Link to="/news">
              <Button size="large" className="lp-btn-ghost" icon={<FileTextOutlined />}>
                ติดตามข่าวสาร
              </Button>
            </Link>
          </div>
          <div className="lp-hero-meta">
            <span>
              <EnvironmentOutlined /> เขตปกครองคณะสงฆ์ ภาค ๔-๕
            </span>
            <span className="lp-hero-meta-dot">•</span>
            <span>
              <DatabaseOutlined /> ข้อมูลวัดและสถิติรายปี
            </span>
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="lp-section lp-features">
        <div className="lp-container">
          <div className="lp-section-head">
            <Text className="lp-section-kicker">ระบบช่วยงานคณะสงฆ์</Text>
            <Title level={2} className="lp-section-title">
              บริหารจัดการข้อมูลครบในที่เดียว
            </Title>
            <div className="lp-gold-divider" />
            <Paragraph className="lp-section-desc">
              ออกแบบสำหรับเจ้าหน้าที่และผู้ดูแลระบบ เพื่อให้การบันทึก ค้นหา และรายงานข้อมูลเป็นไปอย่างถูกต้อง รวดเร็ว
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {FEATURES.map((f) => (
              <Col xs={24} md={8} key={f.title}>
                <div className="lp-feature-card">
                  <div className="lp-feature-icon">{f.icon}</div>
                  <h3 className="lp-feature-title">{f.title}</h3>
                  <p className="lp-feature-desc">{f.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* ===== About ===== */}
      <section className="lp-section lp-about">
        <div className="lp-container">
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={12}>
              <div className="lp-about-emblem">
                <SystemIcon icon={settings.system_icon} size={110} />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <Text className="lp-section-kicker">เกี่ยวกับระบบ</Text>
              <Title level={2} className="lp-section-title">
                สารสนเทศเพื่อการบริหารคณะสงฆ์อย่างเป็นระบบ
              </Title>
              <Paragraph className="lp-about-text">
                ระบบ e-Sangkha รวบรวมข้อมูลสำคัญของวัดและพระสงฆ์ในเขตปกครองคณะสงฆ์
                ภาค ๔-๕ (ธรรมยุต) ไว้อย่างเป็นระเบียบ เพื่อสนับสนุนการจัดทำบัญชีกลางปี
                การรายงานสถิติ และการวางแผนพัฒนาพระพุทธศาสนา
              </Paragraph>
              <ul className="lp-about-list">
                {ABOUT_POINTS.map((point) => (
                  <li key={point}>
                    <CheckCircleOutlined />
                    {point}
                  </li>
                ))}
              </ul>
              <Link to="/login">
                <Button type="primary" className="lp-btn-gold" icon={<ArrowRightOutlined />}>
                  เข้าสู่ระบบเพื่อเริ่มใช้งาน
                </Button>
              </Link>
            </Col>
          </Row>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="lp-cta">
        <div className="lp-cta-inner">
          <Title level={2} className="lp-cta-title">
            พร้อมใช้ระบบจัดการข้อมูลคณะสงฆ์แล้วหรือยัง?
          </Title>
          <Paragraph className="lp-cta-desc">
            เข้าสู่ระบบเพื่อจัดการข้อมูลวัด พระสงฆ์ และสถิติรายปีได้ทันที
          </Paragraph>
          <Link to="/login">
            <Button size="large" className="lp-btn-gold" icon={<LoginOutlined />}>
              เริ่มต้นใช้งาน
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;

